import { useState } from "react";
import { C } from "../lib/colors";
import { PageShell } from "../components/PageShell";
import { Turnstile, isTurnstileConfigured } from "../components/Turnstile";
import { submitToFormspree } from "../lib/formspree";
import { captureLead } from "../lib/leads";
import { SITE } from "../lib/site";

export const ContactPage = ({ onNavigate, authRole }) => {
  const [form, setForm] = useState({name:"",email:"",company:"",subject:"",message:""});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const captchaRequired = isTurnstileConfigured();
  const captchaOk = !captchaRequired || !!captchaToken;

  const handleSubmit = async () => {
    if (!form.name||!form.email||!form.message||!captchaOk) return;
    setLoading(true); setError("");
    try {
      const payload = { ...form, _source: "contact" };
      if (captchaToken) payload["cf-turnstile-response"] = captchaToken;
      await submitToFormspree(payload);
      // Best-effort persist to Supabase so the owner can see the lead in
      // AgencyOS. Failure is non-fatal — Formspree already delivered the email.
      captureLead({
        source:  "contact",
        name:    form.name,
        email:   form.email,
        company: form.company,
        message: form.message,
        payload: { subject: form.subject },
      });
      setSent(true);
    } catch (err) {
      setError(err.message || "Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {width:"100%",background:C.s2,border:`1.5px solid ${C.border}`,borderRadius:10,
    padding:"12px 14px",color:C.text,fontSize:14,fontFamily:"'Instrument Sans',sans-serif",
    transition:"border-color 0.2s"};

  return (
    <PageShell onNavigate={onNavigate} authRole={authRole}>
      <div style={{maxWidth:960,margin:"0 auto",padding:"64px 24px"}}>

        {/* Header */}
        <div style={{marginBottom:56,maxWidth:560}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",
            color:C.green,marginBottom:14,fontFamily:"'DM Mono',monospace",
            display:"flex",alignItems:"center",gap:8}}>
            <span style={{width:20,height:1,background:C.green,display:"inline-block"}}/>
            Get In Touch
          </div>
          <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(32px,5vw,52px)",fontWeight:800,
            letterSpacing:-2,lineHeight:1.08,marginBottom:16}}>
            We'd love to<br/><span style={{color:C.green}}>hear from you.</span>
          </h1>
          <p style={{color:C.muted,fontSize:16,lineHeight:1.7}}>
            Whether you have a question about pricing, want to see a sample of our work, or are ready to get started — we're here and we respond fast.
          </p>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:32,alignItems:"start"}}>

          {/* Left — contact info */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {[
              {icon:"✉",label:"Email Us",value:SITE.contactEmail,sub:"We reply within 2 business hours"},
              {icon:"💬",label:"Live Chat",value:"Available 24/7",sub:"Tap the chat bubble — AI replies in seconds"},
            ].map((item,i)=>(
              <div key={i} style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:14,
                padding:"20px 22px",display:"flex",gap:14,alignItems:"flex-start",
                transition:"border-color 0.2s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=C.border2}
                onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                <div style={{width:42,height:42,borderRadius:11,background:`${C.green}12`,
                  border:`1px solid ${C.green}25`,display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:18,flexShrink:0}}>{item.icon}</div>
                <div>
                  <div style={{fontSize:11,color:C.muted,fontFamily:"'DM Mono',monospace",
                    textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{item.label}</div>
                  <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:3}}>{item.value}</div>
                  <div style={{fontSize:12,color:C.muted}}>{item.sub}</div>
                </div>
              </div>
            ))}

            {/* FAQ shortcut */}
            <div style={{background:`${C.blue}08`,border:`1px solid ${C.blue}20`,borderRadius:14,padding:"18px 22px"}}>
              <div style={{fontSize:13,fontWeight:600,color:"#fff",marginBottom:6}}>Looking for quick answers?</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:12,lineHeight:1.6}}>
                Check our How It Works page — it covers the most common questions about getting started.
              </div>
              <button onClick={()=>onNavigate("how")} style={{background:"transparent",border:`1px solid ${C.blue}40`,
                borderRadius:8,padding:"7px 16px",color:C.blue,fontSize:12,fontWeight:600,
                cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s"}}
                onMouseEnter={e=>e.currentTarget.style.background=`${C.blue}12`}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                Read How It Works →
              </button>
            </div>
          </div>

          {/* Right — form */}
          <div style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:20,padding:32}}>
            {sent ? (
              <div style={{textAlign:"center",padding:"32px 0",animation:"fadeUp 0.4s ease"}}>
                <div style={{width:64,height:64,borderRadius:"50%",background:`${C.green}15`,
                  border:`2px solid ${C.green}`,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:28,margin:"0 auto 20px"}}>✓</div>
                <h3 style={{fontFamily:"'Sora',sans-serif",fontSize:22,fontWeight:800,marginBottom:8}}>Message sent!</h3>
                <p style={{color:C.muted,fontSize:14,lineHeight:1.65,maxWidth:300,margin:"0 auto 24px"}}>
                  Thanks {form.name.split(" ")[0]}! We'll get back to you at {form.email} within 2 business hours.
                </p>
                <button onClick={()=>{setSent(false);setForm({name:"",email:"",company:"",subject:"",message:""});}}
                  style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:9,
                    padding:"9px 22px",color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:700,marginBottom:6}}>Send us a message</div>
                <div style={{fontSize:13,color:C.muted,marginBottom:24}}>We'll get back to you within 2 business hours.</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:14,marginBottom:14}}>
                  {[["name","Your name","First Last"],["email","Email address","you@company.com"]].map(([k,l,p])=>(
                    <div key={k}>
                      <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:"rgba(255,255,255,0.65)"}}>{l}</div>
                      <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={p} type={k==="email"?"email":"text"}
                        style={inputStyle} onFocus={e=>e.target.style.borderColor=C.green} onBlur={e=>e.target.style.borderColor=C.border}/>
                    </div>
                  ))}
                </div>
                {[["company","Company name (optional)","Business name"],["subject","Subject","What can we help you with?"]].map(([k,l,p])=>(
                  <div key={k} style={{marginBottom:14}}>
                    <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:"rgba(255,255,255,0.65)"}}>{l}</div>
                    <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={p}
                      style={inputStyle} onFocus={e=>e.target.style.borderColor=C.green} onBlur={e=>e.target.style.borderColor=C.border}/>
                  </div>
                ))}
                <div style={{marginBottom:22}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:"rgba(255,255,255,0.65)"}}>Message</div>
                  <textarea value={form.message} onChange={e=>set("message",e.target.value)}
                    placeholder="Tell us about your business and what you're looking for…" rows={5}
                    style={{...inputStyle,resize:"vertical",lineHeight:1.65}}
                    onFocus={e=>e.target.style.borderColor=C.green} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
                <Turnstile onToken={setCaptchaToken}/>
                {error && (
                  <div style={{background:"rgba(255,82,82,0.08)",border:"1px solid rgba(255,82,82,0.22)",
                    borderRadius:9,padding:"9px 13px",fontSize:12,color:C.red,marginBottom:14,
                    display:"flex",alignItems:"center",gap:6}}>
                    <span>⚠</span>{error}
                  </div>
                )}
                <button onClick={handleSubmit} disabled={loading||!form.name||!form.email||!form.message||!captchaOk}
                  style={{width:"100%",background:loading||!form.name||!form.email||!form.message||!captchaOk?"rgba(255,255,255,0.06)":C.green,
                    color:loading||!form.name||!form.email||!form.message||!captchaOk?C.muted:"#000",border:"none",borderRadius:10,
                    padding:"13px",fontSize:14,fontWeight:700,cursor:loading||!form.name||!form.email||!form.message||!captchaOk?"not-allowed":"pointer",
                    fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {loading
                    ? <><span style={{width:14,height:14,border:"2px solid rgba(255,255,255,0.2)",borderTopColor:"#fff",
                        borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Sending…</>
                    : "Send Message →"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};
